import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, roleHome } from "../lib/auth";
import { Brand, Spinner } from "../components/Brand";
import { useSEO } from "../lib/seo";

export default function Login() {
    useSEO({
        title: "Sign in — Luxurio Edge Technical Services",
        description: "Sign in to your Luxurio Edge account to book home services or manage your bookings across the UAE.",
    });
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const queryParams = new URLSearchParams(loc.search);
    const expired = queryParams.get("expired") === "1";
    const nextUrl = queryParams.get("next");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const u = await login(email, password);
            if (u.must_change_password) {
                nav("/change-password", { replace: true });
                return;
            }
            // Redirect priority: explicit ?next= query > location.state.from > role home.
            // Only honour ?next= if it looks like an in-app path so we can't be used
            // as an open-redirect.
            const safeNext = nextUrl && nextUrl.startsWith("/") && !nextUrl.startsWith("//")
                ? nextUrl
                : null;
            const from = safeNext || loc.state?.from || roleHome(u.role);
            nav(from, { replace: true });
        } catch (e) {
            setErr(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left brand panel */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#180A21]">
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        backgroundImage:
                            "url('https://static.prod-images.emergentagent.com/jobs/49d685dd-4570-4cf1-ba99-898459f22c0a/images/2eba03b5907160303d253ab9612a23b4c0c36931891302ee36174a6adb6b1fa3.png')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#180A21] via-[#180A21]/85 to-transparent" />
                <div className="relative z-10 p-16 flex flex-col justify-between w-full">
                    <Brand light />
                    <div>
                        <div className="lx-label text-[#D4AF37] mb-6">
                            ★ Trusted by 50,000+ Households
                        </div>
                        <h1 className="font-serif text-5xl text-white leading-tight">
                            Welcome back to
                            <br />
                            <span className="italic lx-gold-text">Luxurio Edge.</span>
                        </h1>
                        <div className="lx-label text-white/50 mt-3 tracking-[0.32em]">
                            Technical Services
                        </div>
                        <p className="text-white/60 mt-6 max-w-md">
                            One platform — customer, technician & operations — engineered as
                            an enterprise marketplace.
                        </p>
                    </div>
                    <div className="text-xs text-white/40 tracking-widest">
                        UAE · INDIA · UK · SINGAPORE · KENYA · GHANA · UGANDA · MAURITIUS
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-10">
                        <Brand />
                    </div>
                    <div className="lx-label text-[#B2912A] mb-3">Sign in</div>
                    <h2 className="font-serif text-4xl text-[#180A21] mb-8">
                        Welcome back.
                    </h2>

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <label className="lx-label text-[#555] mb-2 block">Email</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-white border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition"
                                placeholder="you@email.com"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="lx-label text-[#555] mb-2 block">
                                Password
                            </label>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-white border border-[#E5E2DC] focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition"
                                placeholder="••••••••"
                                autoComplete="current-password"
                            />
                        </div>

                        {expired && !err && (
                            <div
                                data-testid="login-expired-notice"
                                className="text-sm text-[#7A5A00] bg-[#FFF7E0] border border-[#D4AF37] px-4 py-3"
                            >
                                Your session expired. Please sign in again to continue.
                            </div>
                        )}

                        {err && (
                            <div
                                data-testid="login-error"
                                className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            data-testid="login-submit-btn"
                            type="submit"
                            disabled={busy}
                            className="lx-btn-primary w-full py-3.5 font-medium tracking-wide disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                            {busy ? <Spinner /> : "Sign in →"}
                        </button>
                    </form>

                    <p className="mt-8 text-sm text-[#555]">
                        New customer?{" "}
                        <Link
                            data-testid="login-to-register"
                            to={nextUrl ? `/register?next=${encodeURIComponent(nextUrl)}` : "/register"}
                            className="text-[#180A21] underline underline-offset-4 decoration-[#D4AF37] hover:text-[#D4AF37]"
                        >
                            Create an account
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
